import { MapDrawingConfig, drawSelectedRect } from "../canvas-drawing";

import { rectWithPadding } from "../rect-utils";
import { SnapLine } from "../rect-snapping";
import { MapElement } from "../canvas-drawing";
import { Coordinate } from "PosterEditor/hooks/use-coordinates";
import { Rect } from "PosterEditor/model/poster-types";
import { GPSSource } from "PosterEditor/model/poster-project";

export interface DrawDataConfig {
  ctx: any;
  rect: Rect;
  gpsSource: GPSSource;
  color: string;
  padding: number;
  cornerRadius: number;
}

export const drawData = ({
  ctx,
  rect,
  gpsSource,
  color,
  padding,
  cornerRadius,
}: DrawDataConfig): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const source = gpsSource;
  const coordinates: Coordinate[] =
    source?.displayedCoordinates ?? source?.allCoordinates ?? [];
  const elevations = coordinates
    .map((c) => c.elevation)
    .filter((elevation): elevation is number => elevation !== undefined);

  const allZero = elevations.every((e) => e === 0);
  if (elevations.length === 0 || allZero) {
    return {
      elementUnderPointerLocation: undefined,
      snapLines: { vertical: undefined, horiztonal: undefined },
    };
  }

  const relativePadding = (padding / 100) * (rect.width / 10);
  // Draw elevation profile chart
  const chartWidth = rect.width;
  const chartHeight = rect.height - relativePadding;
  const minVisualHeight = 5; // Minimum height in pixels

  const maxElevation = Math.max(...elevations);
  const minElevation = Math.min(...elevations);
  const elevationRange = maxElevation - minElevation || 1;

  const chartX = rect.x;
  const chartY = rect.y + relativePadding;

  const numPoints = elevations.length;
  const sliceCount = Math.min(chartWidth, numPoints);
  const lineSpacing = chartWidth / sliceCount;

  const sampledElevations =
    numPoints > chartWidth
      ? Array.from(
          { length: chartWidth },
          (_, i) => elevations[Math.floor((i / chartWidth) * numPoints)]
        )
      : elevations;

  // sampledElevations.forEach((elevation, index) => {
  //   const normalized = (elevation - minElevation) / elevationRange;
  //   const normalizedHeight =
  //     minVisualHeight + normalized * (chartHeight - minVisualHeight);

  //   const x = Math.round(chartX + index * lineSpacing);
  //   const width = Math.ceil(lineSpacing);
  //   ctx.fillStyle = color;
  //   ctx.fillRect(
  //     x,
  //     Math.round(chartY + chartHeight - normalizedHeight),
  //     width,
  //     Math.round(normalizedHeight)
  //   );
  // });

  // Create rounded clipping path
  ctx.save();
  ctx.beginPath();
  const r = cornerRadius;
  const x = chartX;
  const y = chartY;
  const w = chartWidth;
  const h = chartHeight;

  // Top left
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); // top-right corner
  ctx.arcTo(x + w, y + h, x, y + h, r); // bottom-right corner
  ctx.arcTo(x, y + h, x, y, r); // bottom-left corner
  ctx.arcTo(x, y, x + w, y, r); // top-left corner
  ctx.closePath();
  ctx.clip();

  // Draw bars inside clipped region
  sampledElevations.forEach((elevation, index) => {
    const normalized = (elevation - minElevation) / elevationRange;
    const normalizedHeight =
      minVisualHeight + normalized * (chartHeight - minVisualHeight);

    const barX = Math.round(chartX + index * lineSpacing);
    const barWidth = Math.ceil(lineSpacing);
    const barY = Math.round(chartY + chartHeight - normalizedHeight);
    ctx.fillStyle = color;
    ctx.fillRect(barX, barY, barWidth, Math.round(normalizedHeight));
  });

  ctx.restore(); // restore to undo the clipping
  return {
    elementUnderPointerLocation: undefined,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};
